import { HIRING_RELIABILITY_CONFIG } from "@clipboard-health/contract-backend-main";
import { formatTime } from "@clipboard-health/date-time";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import {
  INTERVIEW_TYPE,
  InterviewStatus,
  type InterviewType,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { capitalize } from "lodash";

import { useUpdateInterview } from "../api/useUpdateInterview";

interface InterviewCancelBottomSheetProps {
  isProspectWorkplace: boolean;
  modalState: UseModalState;
  interviewId: string;
  isLateCancellation?: boolean;
  isSchedulingPastWorkplaceStartHour: boolean;
  workplaceStartHour?: Date;
  attendanceScore: number;
  interviewType: InterviewType;
  onCancelInterview: () => Promise<void>;
}

export function InterviewCancelBottomSheet(props: InterviewCancelBottomSheetProps) {
  const {
    isProspectWorkplace,
    modalState,
    interviewId,
    onCancelInterview,
    isLateCancellation,
    isSchedulingPastWorkplaceStartHour,
    workplaceStartHour,
    attendanceScore,
    interviewType,
  } = props;
  const { showErrorToast, showSuccessToast } = useToast();

  const isPhoneScreen = interviewType === INTERVIEW_TYPE.PHONE_SCREEN;
  const cancelLabel = isPhoneScreen ? "phone call" : "interview";
  const hasAttendanceCredits = attendanceScore > 0;

  const eventContext = {
    interviewId,
    isSchedulingPastWorkplaceStartHour,
    workplaceStartHour,
    isProspectWorkplace,
    attendanceScore,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_CANCEL_MODAL_VIEWED, eventContext);

  const { mutateAsync: cancelInterview, isLoading: isCancelling } = useUpdateInterview({
    onSuccess: async () => {
      showSuccessToast(`${capitalize(cancelLabel)} cancelled`);
      modalState.closeModal();
      await onCancelInterview();
    },
    onError: () => {
      showErrorToast(`An error occurred cancelling ${cancelLabel}`);
    },
  });

  const handleCancel = async () => {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_CANCEL_MODAL_ACTIONS, {
      ...eventContext,
      action: "cancel",
    });

    await cancelInterview({
      interviewId,
      attributes: {
        status: InterviewStatus.CANCELLED,
      },
    });
  };

  const getDescription = (): string | undefined => {
    // Phone screen interviews don't show hiring reliability warnings
    if (!isLateCancellation || isPhoneScreen) {
      return undefined;
    }

    if (isSchedulingPastWorkplaceStartHour && workplaceStartHour) {
      const formattedWorkplaceStartHour = formatTime(workplaceStartHour);
      const restrictedDays = HIRING_RELIABILITY_CONFIG.RESTRICTED_DAYS;
      if (hasAttendanceCredits) {
        return `Cancelling past ${formattedWorkplaceStartHour} is a violation of the attendance policy. However, since you have a strong interview attendance record — we're going to give you a free pass on this one. Your account will not be restricted. If you cancel past ${formattedWorkplaceStartHour} or no-show in the future, you'll be restricted from viewing jobs for ${restrictedDays} days.`;
      }
      return `You're trying to cancel your interview after ${formattedWorkplaceStartHour}. If you cancel or no-show, you'll be restricted from viewing job openings for ${restrictedDays} days.`;
    }

    return undefined;
  };

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="destructive"
            size="large"
            isLoading={isCancelling}
            onClick={handleCancel}
          >
            Cancel
          </LoadingButton>
        </DialogFooter>
      }
    >
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16 }}
        onClick={modalState.closeModal}
      />
      <Stack gap={7} px={7} pb={8} pt="76px" alignItems="center" justifyContent="center">
        <Illustration type="cancellation" />
        <Text variant="h2" textAlign="center">
          Cancel your {cancelLabel}?
        </Text>
        <Text textAlign="center" color={(theme) => theme.palette.text.secondary}>
          {getDescription()}
        </Text>
      </Stack>
    </BottomSheet>
  );
}
